import { logger } from "./logger";

/**
 * Attach audit logging to a PouchDB replication/sync handler so we can see whether
 * a device's data actually reaches the server. Logs only the health-critical events
 * (low volume); a per-`change` heartbeat is intentionally NOT logged to avoid flood.
 *
 * IMPORTANT: never logs the couch URL — it embeds admin credentials. Only the
 * `remoteDb` name (e.g. "payments" or "w..._payments") is recorded.
 *
 * @param {object} rep      the object returned by PouchDB.sync / .replicate
 * @param {string} localDb  local DB name (used as the log `entity`)
 * @param {string} remoteDb remote DB NAME (not the full URL)
 */
export function attachReplicationLogging(rep, localDb, remoteDb) {
  if (!rep || typeof rep.on !== "function") return rep;

  let lastStall = 0;

  rep
    .on("denied", (e) =>
      // A per-doc rejection: the server REFUSED the doc -> that data is NOT saved
      // server-side. The single most important "silent data loss" signal.
      logger.error("sync.replication_denied", "Server rejected replicated docs (data not saved)", {
        entity: localDb,
        context: { remote: remoteDb, error: String(e?.message || e) },
      })
    )
    .on("error", (e) =>
      logger.error("sync.replication_error", "Replication error", {
        entity: localDb,
        context: { remote: remoteDb, error: String(e?.message || e) },
      })
    )
    .on("paused", (e) => {
      if (!e) return; // paused with no error = caught up / healthy — don't log
      if (Date.now() - lastStall < 120000) return; // throttle: at most once / 2 min per handler
      lastStall = Date.now();
      logger.warning("sync.replication_stalled", "Replication paused with error (can't reach server)", {
        entity: localDb,
        context: { remote: remoteDb, error: String(e?.message || e) },
      });
    });

  return rep;
}
